import { useNavigate, useLocation } from 'react-router-dom';
import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthCredentialsDTO, AuthUser, UserInfo } from '../features/auth/types';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import fetchApi from '../lib/fetchApi';
import { useDispatch } from 'react-redux';
import { resetCurrentUser, setCurrentUser } from '../redux/currentUserSlice';

export const loginUser = async (credentials: AuthCredentialsDTO): Promise<AuthUser> => {
    const response = await fetchApi.post<AuthUser>('/auth/login', {
        Email: credentials.email,
        Password: credentials.password,
    });
    return response;
};

export const logoutUser = async (): Promise<void> =>
    await fetchApi.post('/auth/logout');

export const decodeToken = (token: string): any => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

export const isTokenExpired = (token: string): boolean => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
};

export const syncNotifications = async (
    onSync?: (hasNotifications: boolean) => void
): Promise<boolean | undefined> => {
    try {
        const hasNotifications = await fetchApi.get<boolean>('/notification/syncNotifications');
        localStorage.setItem('hasNotifications', String(hasNotifications));
        onSync?.(hasNotifications);
        return hasNotifications;
    } catch (error) {
        console.error('Error syncing notifications:', error);
        return undefined;
    }
};

export interface UserContextType {
    userInfo: UserInfo;
    setUserInfo: (newUserInfo: UserInfo & { idUser?: string }) => void;
    isAuthenticated: boolean;
    loading: boolean;
    logout: () => void;
    syncNotifications: () => Promise<boolean | undefined>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

const POLLING_INTERVAL_MS = 30_000; // 30 segundos

const UserProviderInner: React.FC<UserProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [userInfo, setUserInfoState] = useState<UserInfo>({
        id: '',
        username: '',
        nom: '',
        cognom: '',
        cognomUsuari: '',
        email: '',
        idClient: 0,
        nameClient: '',
        language: '',
        nomUsuari: '',
    });

    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    // ─── Polling: sincronizar hasNotifications con la API ────────────────────
    const syncNotificationsWithState = useCallback(async () => {
        return await syncNotifications((hasNotifications) => {
            setUserInfoState(prev => ({ ...prev, hasNotifications }));
        });
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        void syncNotificationsWithState(); // llamada inmediata al autenticarse

        const interval = setInterval(() => {
            void syncNotificationsWithState();
        }, POLLING_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [isAuthenticated, syncNotificationsWithState]);
    // ────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        const checkSession = () => {
            const token = localStorage.getItem('token');
            const publicRoutes = ['/login', '/register', '/forgot-password', '/restore', '/restoreEmail', '/claimFollowUp'];
            const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

            if (token && !isTokenExpired(token)) {
                setIsAuthenticated(true);
                const decoded = decodeToken(token);
                if (decoded) {
                    const userData: UserInfo = {
                        id: decoded.userId || '',
                        username: decoded.username || '',
                        nom: decoded.nom || decoded.firstName || '',
                        cognom: decoded.cognom || decoded.lastName || '',
                        cognomUsuari: decoded.cognomUsuari || '',
                        email: decoded.email || '',
                        idClient: decoded.clientId || 0,
                        nameClient: decoded.nameClient || '',
                        language: decoded.language || '',
                        nomUsuari: decoded.nomUsuari || '',
                        clientLogo: localStorage.getItem('clientLogo') || '',
                        clientLogoContentType: localStorage.getItem('clientLogoContentType') || '',
                        hasNotifications: localStorage.getItem('hasNotifications') == 'True'
                    };
                    setUserInfoState(userData);
                    localStorage.setItem('user', JSON.stringify(userData));
                }
            } else {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setIsAuthenticated(false);
                if (!isPublicRoute) {
                    navigate('/login', { replace: true });
                }
            }

            setLoading(false);
        };

        checkSession();
    }, [navigate, location.pathname]);

    const setUserInfo = useCallback((newUserData: UserInfo & { idUser?: string }) => {
        setUserInfoState(newUserData);
        if (newUserData?.id) {
            localStorage.setItem('user', JSON.stringify(newUserData));
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('hasNotifications');
        setUserInfoState({
            id: '',
            username: '',
            nom: '',
            cognom: '',
            cognomUsuari: '',
            email: '',
            idClient: 0,
            nameClient: '',
            language: '',
            nomUsuari: '',
        });
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
    }, [navigate]);

    const contextValue: UserContextType = {
        userInfo,
        setUserInfo,
        isAuthenticated,
        loading,
        logout,
        syncNotifications: syncNotificationsWithState,
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <UserContext.Provider value={contextValue}>
            {children}
        </UserContext.Provider>
    );
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    return <UserProviderInner>{children}</UserProviderInner>;
};

export const useAuth = ({ config }: { config?: MutationOptions<AuthUser, Error, AuthCredentialsDTO> } = {}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const login = (data: AuthUser) => {
        localStorage.setItem('token', data.token);
        localStorage.setItem('permissions', JSON.stringify(data.permissions));
        localStorage.setItem('roles', JSON.stringify(data.roles));
        localStorage.setItem('clientLogo', data.clientLogo || '');
        localStorage.setItem('clientLogoContentType', data.clientLogoContentType || '');
        localStorage.setItem('hasNotifications', String(data.hasNotifications ?? false));
        localStorage.setItem('isSuperAdmin', JSON.stringify(data.isSuperAdmin));

        i18n.changeLanguage(data.language);

        data.selectedOrganizationalStructureIds = [];
        dispatch(setCurrentUser(data))
        navigate('/claims/dashboard');
    };

    const logout = async () => {
        dispatch(resetCurrentUser());

        queryClient.clear();
        await logoutUser();
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const { mutateAsync: authenticate, isError, error } = useMutation<AuthUser, Error, AuthCredentialsDTO, unknown>({
        mutationFn: loginUser,
        ...config,
        onSuccess: (data, variables, onMutateResult, context) => {
            login(data);
            config?.onSuccess?.(data, variables, onMutateResult, context);
        },
    });

    return {
        loginUser,
        authenticate,
        isError,
        error,
        logout,
    };
};

export function useToken() {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return {
        raw: token,
        decoded: decodeToken(token),
        isExpired: isTokenExpired(token),
    };
}

export function useUser() {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error(
            'useUser must be used within a UserProvider. ' +
            'Make sure your component is wrapped with <UserProvider>.'
        );
    }
    return context;
}

export function useUserPermissions() {
    const permissionsString = localStorage.getItem('permissions');
    try {
        return permissionsString ? JSON.parse(permissionsString) : [];
    } catch (error) {
        console.error('Failed to parse permissions:', error);
        return [];
    }
}

export const SUPER_ADMIN_ROLE = 'SuperAdministrator';

export function useUserRoles(): string[] {
    const rolesString = localStorage.getItem('roles');
    try {
        return rolesString ? JSON.parse(rolesString) : [];
    } catch (error) {
        console.error('Failed to parse roles:', error);
        return [];
    }
}

export function useIsSuperAdmin(): boolean {
    const isSuperAdminString = localStorage.getItem('isSuperAdmin');
    try {
        return isSuperAdminString ? JSON.parse(isSuperAdminString) : false;
    } catch (error) {
        console.error('Failed to parse is super admin:', error);
        return false;
    }
}

export function useLanguageUser() {
    const { i18n } = useTranslation();
    const language = i18n?.language;
    if (language === undefined) {
        throw new Error('useLanguageUser must have a language');
    }
    return language;
}
